import { supabase } from '../lib/supabase';

export interface SubscriptionPlan {
  id: string;
  name: string;
  stripePriceId: string;
  stripeProductId: string;
  interval: 'month' | 'year';
  price: number;
  currency: string;
  description: string;
  // Update to use exact values from Stripe metadata, plus free/vip with consistent lowercase
  tier?: 'free' | 'pro' | 'team_small' | 'team_medium' | 'team_large' | 'vip';
  maxCollaborators?: number | 'unlimited';
  features?: string[];
  isTeamPlan?: boolean;
}

export const SUBSCRIPTION_PLANS: Record<string, SubscriptionPlan> = {
  // Pro Monthly Plan (solo)
  PRO_MONTHLY: {
    id: 'pro_monthly',
    name: 'Pro Monthly',
    stripePriceId: 'price_1QxF1hKFev5yZqFyoaMSaKNk',
    stripeProductId: 'prod_Rqwv9uDQiYctEG',
    interval: 'month',
    price: 5.0,
    currency: 'eur',
    description: 'Unlimited projects for individuals',
    tier: 'pro',
    maxCollaborators: 0,
    isTeamPlan: false,
    features: ['Unlimited projects', 'Full AI features', 'Personal use only'],
  },

  // Pro Annual Plan (solo)
  PRO_ANNUAL: {
    id: 'pro_annual',
    name: 'Pro Annual',
    stripePriceId: 'price_1QxF1dKFev5yZqFyFjGoZ4CX',
    stripeProductId: 'prod_Rqwvpsa0JaKXFA',
    interval: 'year',
    price: 50.0,
    currency: 'eur',
    description: 'Unlimited projects for individuals, billed annually (Save ~16%)',
    tier: 'pro',
    maxCollaborators: 0,
    isTeamPlan: false,
    features: ['Unlimited projects', 'Full AI features', 'Personal use only'],
  },

  // Small Team Monthly Plan (Starter)
  SMALL_TEAM_MONTHLY: {
    id: 'small_team_monthly',
    name: 'Starter Team Monthly',
    stripePriceId: 'price_1RDwE1KFev5yZqFy4dE8A44u',
    stripeProductId: 'prod_S8CdVesO8gyxSO',
    interval: 'month',
    price: 20.0,
    currency: 'eur',
    description: 'Team collaboration with up to 5 members',
    tier: 'team_small',
    maxCollaborators: 5,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 5 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
    ],
  },

  // Small Team Annual Plan (Starter)
  SMALL_TEAM_ANNUAL: {
    id: 'small_team_annual',
    name: 'Starter Team Annual',
    stripePriceId: 'price_1RDwEtKFev5yZqFy4PYfJNbH',
    stripeProductId: 'prod_S8CecmKausFENt',
    interval: 'year',
    price: 200.0,
    currency: 'eur',
    description: 'Team collaboration with up to 5 members, billed annually (Save ~16%)',
    tier: 'team_small',
    maxCollaborators: 5,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 5 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
    ],
  },

  // Medium Team Monthly Plan (Growth)
  MEDIUM_TEAM_MONTHLY: {
    id: 'medium_team_monthly',
    name: 'Growth Team Monthly',
    stripePriceId: 'price_1RDwGSKFev5yZqFyfpLNE976',
    stripeProductId: 'prod_S8CfXIT51997mH',
    interval: 'month',
    price: 50.0,
    currency: 'eur',
    description: 'Team collaboration with up to 20 members',
    tier: 'team_medium',
    maxCollaborators: 20,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 20 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
      'Advanced analytics',
    ],
  },

  // Medium Team Annual Plan (Growth)
  MEDIUM_TEAM_ANNUAL: {
    id: 'medium_team_annual',
    name: 'Growth Team Annual',
    stripePriceId: 'price_1RDwGvKFev5yZqFyvsj5pu84',
    stripeProductId: 'prod_S8CgV6EjcmRjV0',
    interval: 'year',
    price: 500.0,
    currency: 'eur',
    description: 'Team collaboration with up to 20 members, billed annually (Save ~16%)',
    tier: 'team_medium',
    maxCollaborators: 20,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 20 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
      'Advanced analytics',
    ],
  },

  // Large Team Monthly Plan (Gold)
  LARGE_TEAM_MONTHLY: {
    id: 'large_team_monthly',
    name: 'Gold Team Monthly',
    stripePriceId: 'price_1RDwI1KFev5yZqFyhVojqHF2',
    stripeProductId: 'prod_S8ChbzNOhsStum',
    interval: 'month',
    price: 100.0,
    currency: 'eur',
    description: 'Team collaboration with up to 50 members',
    tier: 'team_large',
    maxCollaborators: 50,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 50 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
      'Advanced analytics',
      'Custom integrations',
    ],
  },

  // Large Team Annual Plan (Gold)
  LARGE_TEAM_ANNUAL: {
    id: 'large_team_annual',
    name: 'Gold Team Annual',
    stripePriceId: 'price_1RDwIMKFev5yZqFyithE92mq',
    stripeProductId: 'prod_S8Chk6NlbYmVfn',
    interval: 'year',
    price: 1000.0,
    currency: 'eur',
    description: 'Team collaboration with up to 50 members, billed annually (Save ~16%)',
    tier: 'team_large',
    maxCollaborators: 50,
    isTeamPlan: true,
    features: [
      'Unlimited projects',
      'Up to 50 team members',
      'Team collaboration features',
      'Full AI features',
      'Priority support',
      'Advanced analytics',
      'Custom integrations',
    ],
  },
};

// Define subscription tier type for consistent typing
interface SubscriptionTier {
  maxProjects: number | 'unlimited';
  maxCollaborators: number | 'unlimited';
}

export const SUBSCRIPTION_TIERS: Record<string, SubscriptionTier> = {
  // Standardized to lowercase consistent with Stripe metadata
  free: {
    maxProjects: 3,
    maxCollaborators: 0, // No sharing allowed on free plan
  },
  pro: {
    maxProjects: 'unlimited',
    maxCollaborators: 0, // Solo use only
  },
  team_small: {
    maxProjects: 'unlimited',
    maxCollaborators: 5,
  },
  team_medium: {
    maxProjects: 'unlimited',
    maxCollaborators: 20,
  },
  team_large: {
    maxProjects: 'unlimited',
    maxCollaborators: 50,
  },
  vip: {
    maxProjects: 'unlimited',
    maxCollaborators: 'unlimited',
  },
};

// Utility function to get a plan by its price ID
export function getPlanByPriceId(priceId: string): SubscriptionPlan | undefined {
  return Object.values(SUBSCRIPTION_PLANS).find(plan => plan.stripePriceId === priceId);
}

// Utility function to format price with currency
export function formatPrice(price: number, currency: string = 'eur'): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(price);
}

// Utility function to compare plans
export function comparePlans(plan1: SubscriptionPlan, plan2: SubscriptionPlan): number {
  return plan1.price - plan2.price;
}

// Determine the best value plan
export function getBestValuePlan(): SubscriptionPlan {
  const plans = Object.values(SUBSCRIPTION_PLANS);
  return plans.reduce((best, current) => (comparePlans(current, best) < 0 ? current : best));
}

// Get max collaborators for a subscription tier
export function getMaxCollaborators(tier: string | null | undefined): number | 'unlimited' {
  if (!tier) return 0;

  // Normalize the tier name to lowercase for consistent lookup
  const normalizedTier = tier.toLowerCase();
  
  // Handle VIP users explicitly
  if (normalizedTier === 'vip') return 'unlimited';

  const tierConfig = SUBSCRIPTION_TIERS[normalizedTier as keyof typeof SUBSCRIPTION_TIERS];
  if (!tierConfig) return 0;

  return tierConfig.maxCollaborators;
}

// Get max projects for a subscription tier
export function getMaxProjects(tier: string | null | undefined): number | 'unlimited' {
  if (!tier) return 3; // Default to free tier limit
  
  // Normalize the tier name to lowercase for consistent lookup
  const normalizedTier = tier.toLowerCase();
  
  // Handle VIP users explicitly
  if (normalizedTier === 'vip') return 'unlimited';

  const tierConfig = SUBSCRIPTION_TIERS[normalizedTier as keyof typeof SUBSCRIPTION_TIERS];
  if (!tierConfig) return 3; // Default to free tier limit

  return tierConfig.maxProjects;
}

// Check if user can invite more collaborators
export function canInviteCollaborators(
  tier: string | null | undefined,
  currentCollaboratorCount: number
): boolean {
  // VIP users always can invite collaborators regardless of count
  // Make sure to handle case-insensitive comparison
  if (tier && tier.toLowerCase() === 'vip') return true;
  
  const maxCollaborators = getMaxCollaborators(tier);

  // Users with unlimited collaborators can always invite
  if (maxCollaborators === 'unlimited') return true;

  // Otherwise check against the limit
  return currentCollaboratorCount < maxCollaborators;
}

/**
 * Checks if user can downgrade to a specific tier based on their current usage
 * 
 * @param targetTier The tier the user wants to downgrade to
 * @param projectCount Number of projects the user currently has
 * @param collaboratorCount Number of collaborators the user currently has
 * @returns An object with validation result and reason if invalid
 */
export function canDowngradeToPlan(
  targetTier: string,
  projectCount: number,
  collaboratorCount: number
): { valid: boolean; reason?: string } {
  // Get limits for the target tier
  const maxProjects = getMaxProjects(targetTier);
  const maxCollaborators = getMaxCollaborators(targetTier);
  
  // Check project limits
  if (maxProjects !== 'unlimited' && projectCount > maxProjects) {
    return { 
      valid: false, 
      reason: `You have ${projectCount} projects, but the ${getTierDisplayName(targetTier)} plan only allows ${maxProjects} projects.`
    };
  }
  
  // Check collaborator limits
  if (maxCollaborators !== 'unlimited' && collaboratorCount > maxCollaborators) {
    return { 
      valid: false, 
      reason: `You have ${collaboratorCount} collaborators, but the ${getTierDisplayName(targetTier)} plan only allows ${maxCollaborators} collaborators.`
    };
  }
  
  // All checks passed
  return { valid: true };
}

/**
 * Gets a display-friendly name for a subscription tier
 */
export function getTierDisplayName(tier: string): string {
  switch (tier) {
    case 'free':
      return 'Free';
    case 'pro':
      return 'Pro';
    case 'team_small':
      return 'Team Starter';
    case 'team_medium':
      return 'Team Growth';
    case 'team_large':
      return 'Team Gold';
    case 'vip':
      return 'VIP';
    default:
      return tier.charAt(0).toUpperCase() + tier.slice(1).replace(/_/g, ' ');
  }
}

/**
 * Interface for subscription usage statistics
 */
export interface SubscriptionUsage {
  projectCount: number;
  collaboratorCount: number;
  currentTier: string | null;
}

/**
 * Gets the current user's subscription usage statistics
 * Uses the secure Supabase Edge Function for subscription details
 * @returns SubscriptionUsage object with project count, collaborator count and current tier
 */
export async function getCurrentSubscriptionUsage(): Promise<SubscriptionUsage> {
  // Default response with empty values
  const defaultResponse: SubscriptionUsage = {
    projectCount: 0,
    collaboratorCount: 0,
    currentTier: null
  };
  
  try {
    // Get current user and session
    const { data: { user } } = await supabase.auth.getUser();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!user || !session) {
      console.error('No authenticated user or session found');
      return defaultResponse;
    }
    
    // Fetch projects count (non-archived projects only)
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id', { count: 'exact' })
      .eq('archived', false);
      
    if (projectsError) {
      console.error('Error fetching project count:', projectsError);
      return defaultResponse;
    }
    
    const projectCount = projects?.length || 0;
    
    // Call the secure Supabase Edge Function for collaborator permissions
    // This will return subscription tier and collaborator count
    const { data, error } = await supabase.functions.invoke('check-collaborator-permissions', {
      body: JSON.stringify({ userId: user.id }),
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    
    if (error) {
      console.error('Error checking collaborator permissions:', error);
      return {
        projectCount,
        collaboratorCount: 0,
        currentTier: null
      };
    }
    
    return {
      projectCount,
      collaboratorCount: data.totalCollaboratorCount || 0,
      currentTier: data.currentTier || null
    };
  } catch (error) {
    console.error('Error fetching subscription usage:', error);
    return defaultResponse;
  }
}
